<template>
    <TransitionRoot appear :show="isProfileModalOpen" as="template">
        <Dialog as="div" class="relative z-50" @close="closeModal">
            <TransitionChild as="template" enter="duration-200 ease-out" enter-from="opacity-0" enter-to="opacity-100"
                leave="duration-150 ease-in" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black/60 backdrop-blur-md" />
            </TransitionChild>

            <div class="fixed inset-0 flex items-center justify-center p-4">
                <TransitionChild as="template" enter="duration-200 ease-out" enter-from="opacity-0 scale-95"
                    enter-to="opacity-100 scale-100" leave="duration-150 ease-in" leave-from="opacity-100 scale-100"
                    leave-to="opacity-0 scale-95">
                    <DialogPanel
                        class="w-full max-w-md rounded-2xl bg-nera-elevated-1 border border-nera-light-border shadow-2xl overflow-hidden">
                        <!-- Loading skeleton -->
                        <template v-if="isLoadingPlan">
                            <div class="p-8">
                                <div class="flex items-center gap-4 mb-6">
                                    <div class="h-12 w-12 rounded-full bg-nera-light-hover animate-pulse" />
                                    <div class="flex-1 space-y-2">
                                        <div class="h-4 w-32 rounded bg-nera-light-hover animate-pulse" />
                                        <div class="h-3 w-48 rounded bg-nera-light-hover animate-pulse" />
                                    </div>
                                </div>
                                <div class="h-20 rounded-xl bg-nera-elevated-3 animate-pulse mb-6" />
                                <div class="h-11 rounded-xl bg-nera-light-hover animate-pulse" />
                            </div>
                        </template>

                        <!-- Profile view -->
                        <template v-else-if="profileState === 'view'">
                            <!-- Header -->
                            <div class="px-8 pt-8 pb-6">
                                <!-- User info -->
                                <div class="flex items-center gap-4 mb-6">
                                    <div
                                        class="flex h-12 w-12 items-center justify-center rounded-full bg-nera-light-hover text-nera-light-text-strong text-base font-semibold shrink-0">
                                        {{ userInitials }}
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-md font-semibold text-nera-text-light truncate">
                                            {{ authStore.user?.first_name || "—" }} {{ authStore.user?.last_name || ""
                                            }}
                                        </p>
                                        <p class="text-xs text-nera-muted-light truncate">
                                            {{ authStore.user?.email || "—" }}
                                        </p>
                                    </div>
                                    <button @click="closeModal"
                                        class="ml-auto flex h-7 w-7 items-center justify-center rounded-lg! text-nera-muted-light! hover:text-nera-light-text-strong! hover:bg-nera-light-hover! transition-colors! focus:outline-none">
                                        <i class="fas fa-times text-sm"></i>
                                    </button>
                                </div>

                                <!-- Plan card -->
                                <div class="rounded-xl bg-nera-elevated-2 border border-nera-light-border p-4 mb-6">
                                    <div class="flex items-center justify-between mb-1">
                                        <p class="text-xs font-medium text-nera-muted-light uppercase tracking-widest">
                                            Current plan
                                        </p>
                                        <span v-if="currentPlan" class="text-xs font-semibold px-2 py-0.5 rounded-full!"
                                            :class="planBadgeClass">
                                            {{ planStatusLabel }}
                                        </span>
                                    </div>
                                    <div class="flex items-baseline justify-between !mt-1 !mb-0">
                                        <p class="text-lg font-bold text-nera-text-light">
                                            {{ currentPlan?.name || "No Active Plan" }}
                                        </p>
                                        <span v-if="expiryLabel" class="!text-xs !text-nera-light-text-strong shrink-0 !ml-3">
                                            {{ expiryLabel }}
                                        </span>
                                    </div>
                                    <p v-if="currentPlan?.description" class="!text-xs !text-nera-muted-light !mt-2 !mb-0">
                                        {{ currentPlan?.description }}
                                    </p>
                                    <!-- divider -->
                                    <div class="!mt-3 !mb-0 border-t border-nera-light-border" />
                                    <UsagePar class="!px-0 !py-0 !pt-3" />
                                </div>
                                <!-- Cancel button — only shown if they have a cancellable plan -->
                                <button v-if="canCancel" @click="profileState = 'confirm'"
                                    class="w-full rounded-xl! border! border-nera-light-border! bg-transparent! p-3 text-sm font-medium text-red-600! hover:bg-red-50! hover:border-red-300! transition-colors! focus:outline-none">
                                    Cancel subscription
                                </button>

                                <button v-else @click="closeModal"
                                    class="w-full rounded-xl border-[0.5px] border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-dark! hover:bg-nera-accent-hover! transition-colors focus:outline-none">
                                    Done
                                </button>
                            </div>
                        </template>

                        <!-- Confirm cancellation -->
                        <template v-else-if="profileState === 'confirm'">
                            <div class="p-8 text-center">
                                <div
                                    class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 text-xl">
                                    <i class="fas fa-exclamation-triangle"></i>
                                </div>
                                <DialogTitle class="text-xl font-semibold text-nera-text-light">
                                    Cancel subscription?
                                </DialogTitle>
                                <p class="mt-2 mb-8! text-sm text-nera-muted-light">
                                    You'll lose access to your
                                    <span class="text-nera-light-text-strong font-medium">{{ currentPlan?.name }}</span>
                                    plan benefits at the end of your billing period.
                                </p>

                                <button @click="handleCancelSubscription" :disabled="isCancelling"
                                    class="w-full rounded-xl border-[0.5px]! border-transparent! bg-nera-danger! p-3 text-sm font-bold text-nera-text-dark! hover:bg-nera-danger-hover! transition-colors focus:outline-none disabled:opacity-50! disabled:cursor-not-allowed!">
                                    <span v-if="isCancelling">
                                        <i class="fas fa-circle-notch fa-spin mr-2"></i>Cancelling…
                                    </span>
                                    <span v-else>Yes, cancel subscription</span>
                                </button>

                                <button @click="profileState = 'view'" :disabled="isCancelling"
                                    class="mt-3 w-full rounded-xl border-[0.5px]! border-nera-light-border! bg-transparent! p-3 text-sm font-medium text-nera-light-text-strong! hover:bg-nera-light-hover! transition-colors focus:outline-none! disabled:opacity-50!">
                                    Keep my plan
                                </button>
                            </div>
                        </template>

                        <!-- Success -->
                        <template v-else-if="profileState === 'success'">
                            <div class="p-8 text-center">
                                <div
                                    class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-xl">
                                    <i class="fas fa-check"></i>
                                </div>
                                <DialogTitle class="text-xl font-semibold text-nera-text-light">
                                    Subscription cancelled
                                </DialogTitle>
                                <p class="mt-2! mb-8! text-sm text-nera-muted-light">
                                    Your plan has been cancelled. You'll retain access until the end of your current
                                    billing period.
                                </p>
                                <button @click="closeModal"
                                    class="w-full rounded-xl border-[0.5px]! border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-dark! hover:bg-nera-accent-hover! transition-colors focus:outline-none">
                                    Done
                                </button>
                            </div>
                        </template>

                        <!-- Error -->
                        <template v-else-if="profileState === 'error'">
                            <div class="p-8 text-center">
                                <div
                                    class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 text-xl">
                                    <i class="fas fa-times"></i>
                                </div>
                                <DialogTitle class="text-xl font-semibold text-nera-text-light">
                                    Something went wrong
                                </DialogTitle>
                                <p class="mt-2 mb-8 text-sm text-nera-muted-light">
                                    We couldn't cancel your subscription. Please try again or contact support.
                                </p>
                                <button @click="handleCancelSubscription"
                                    class="w-full rounded-xl border-[0.5px] border-transparent bg-nera-accent p-3 text-sm font-bold text-nera-text-dark hover:bg-nera-accent-hover transition-colors focus:outline-none">
                                    Try again
                                </button>
                                <button @click="closeModal"
                                    class="mt-3 w-full rounded-xl border-[0.5px] border-nera-light-border bg-transparent p-3 text-sm font-medium text-nera-muted-light hover:bg-nera-light-hover transition-colors focus:outline-none">
                                    Dismiss
                                </button>
                            </div>
                        </template>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, computed, defineExpose } from "vue";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
} from "@headlessui/vue";
import { useAuthStore } from "../stores/auth";
import UsagePar from "./UsagePar.vue";

const authStore = useAuthStore();

const isProfileModalOpen = ref(false);
const profileState = ref("view"); // 'view' | 'confirm' | 'success' | 'error'
const currentPlan = ref(null);
const isLoadingPlan = ref(false);
const isCancelling = ref(false);


const userInitials = computed(() => {
    const name = authStore.user?.first_name || "";
    return name.charAt(0).toUpperCase() || "—";
});

const canCancel = computed(() => {
    if (!currentPlan.value) return false;
    if (currentPlan.value.pending_cancellation) return false;
    // Don't show cancel for free/permanent plans
    return !currentPlan.value.free_permanent;
});

const expiryLabel = computed(() => {
    const raw = currentPlan.value?.expires_at;
    if (!raw) return null;
    const date = new Date(raw);
    const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (currentPlan.value?.pending_cancellation) return `Ends ${formatted}`;
    if (currentPlan.value?.isTrial) return `Trial ends ${formatted}`;
    return `Renews ${formatted}`;
});

const planBadgeClass = computed(() => {
    if (currentPlan.value?.pending_cancellation) return "bg-nera-light-hover text-nera-muted-light!";
    if (currentPlan.value?.isTrial) return "bg-amber-100 text-amber-700!";
    return "bg-emerald-100 text-emerald-700!";
});

const planStatusLabel = computed(() => {
    if (!currentPlan.value) return "";
    if (currentPlan.value.pending_cancellation) return "Cancelled";
    if (currentPlan.value.isTrial) return "Trial";
    return "Active";
});

const openModal = async () => {
    profileState.value = "view";
    isProfileModalOpen.value = true;
    await fetchCurrentPlan();
};

defineExpose({ openModal });

const closeModal = () => {
    isProfileModalOpen.value = false;
    setTimeout(() => {
        profileState.value = "view";
        isCancelling.value = false;
    }, 300);
};

const fetchCurrentPlan = async () => {
    isLoadingPlan.value = true;
    try {
        const response = await fetch("/api/current_plan", {
            headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch plan");
        const data = await response.json();
        console.log("Current plan:", data);
        currentPlan.value = data;
    } catch (err) {
        console.error(err);
        currentPlan.value = null;
    } finally {
        isLoadingPlan.value = false;
    }
};

const handleCancelSubscription = async () => {
    isCancelling.value = true;
    try {
        const response = await fetch("/api/subscriptions/cancel", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content || "",
            },
        });
        if (!response.ok) throw new Error("Failed to cancel subscription");
        await authStore.fetchCurrentUser();
        profileState.value = "success";
    } catch (err) {
        console.error(err);
        profileState.value = "error";
    } finally {
        isCancelling.value = false;
    }
};
</script>